import React from 'react';
import PeopleLensLogo from '../../../assets/HeaderIcons/PeopleLens_Logo.svg';
import NUDGE_ICON from '../../../assets/nudge.png';
import INSIGHT_ICON from '../../../assets/research.png';
import IMPACT_ICON from '../../../assets/impact.png';

const s: Record<string, React.CSSProperties> = {
  outer: {
    background: 'rgba(217,217,217,0.4)',
    padding: '24px 16px',
    minHeight: '100vh',
  },
  container: {
    fontFamily: 'Poppins, Rubik, -apple-system, sans-serif',
    fontWeight: 400,
    maxWidth: 700,
    margin: '0 auto',
    background: '#ffffff',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  headerBlock: {
    padding: '16px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  logoImg: { height: 30, display: 'block' },
  body: { padding: '20px 28px' },
  greeting: { fontSize: 13, fontWeight: 400, color: '#171F46', lineHeight: 1.6, marginBottom: 6 },
  sectionHeader: {
    background: '#80DEEA',
    padding: '10px 14px',
    borderRadius: 4,
    margin: '24px 0 12px 0',
  },
  sectionTitle: { fontWeight: 700, fontSize: 14, color: '#171F46', margin: 0 },
  ul: { paddingLeft: 20, paddingRight: 14, margin: '8px 0' },
  li: { marginBottom: 10, fontSize: 13, fontWeight: 400, lineHeight: 1.6, color: '#171F46' },
  table: { width: '100%', borderCollapse: 'collapse' as const, margin: '12px 0', fontSize: 12 },
  th: {
    background: 'rgba(232,236,239,0.71)',
    color: '#171F46',
    padding: '10px 8px',
    textAlign: 'left' as const,
    fontWeight: 700,
  },
  td: { padding: '10px 8px', borderBottom: '1px solid #e5e7eb', color: '#333', fontWeight: 400, verticalAlign: 'top' as const },
  tdAlt: { padding: '10px 8px', borderBottom: '1px solid #e5e7eb', color: '#333', fontWeight: 400, background: '#f9fafb', verticalAlign: 'top' as const },
  tdBold: { padding: '10px 8px', borderBottom: '1px solid #e5e7eb', color: '#171F46', fontWeight: 700, background: '#ffffff', verticalAlign: 'top' as const },
  iconRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
    padding: '10px 14px',
    background: '#f9fafb',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
  },
  iconImg: { width: 32, height: 32, objectFit: 'contain' as const, flexShrink: 0 },
  iconText: { fontSize: 13, fontWeight: 400, color: '#374151', lineHeight: 1.55 },
  scoreChip: {
    display: 'inline-block',
    background: '#366FF5',
    color: '#fff',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 600,
    marginLeft: 8,
  },
  snippetBox: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 400,
    color: '#1e40af',
    fontStyle: 'italic',
    marginTop: 8,
  },
  apolloLink: { display: 'inline-block', marginTop: 6, fontSize: 12, color: '#366FF5' },
  signature: { fontSize: 13, fontWeight: 400, color: '#171F46', marginTop: 20, lineHeight: 1.7 },
  loginBtn: {
    display: 'inline-block', background: '#0B69FF', color: '#fff',
    padding: '8px 20px', borderRadius: 22, fontSize: 13, fontWeight: 700,
    textDecoration: 'none', marginRight: 6, marginTop: 4,
    whiteSpace: 'nowrap' as const,
  },
  resourceBtn: {
    display: 'inline-block', background: '#facc15', color: '#171F46',
    padding: '8px 20px', borderRadius: 22, fontSize: 13, fontWeight: 700,
    textDecoration: 'none', marginRight: 6, marginTop: 4,
    whiteSpace: 'nowrap' as const,
  },
};

interface WeeklyReportViewProps { data: any; }

const H = ({ html }: { html: string }) => (
  <span dangerouslySetInnerHTML={{ __html: html || '' }} />
);

const FmtCell = ({ cur, prev, prefix = '', suffix = '' }: { cur: any; prev?: any; prefix?: string; suffix?: string }) => {
  if (cur == null && prev == null) return <>—</>;
  const addSign = (v: any): string => {
    if (v == null) return '—';
    const s = String(v);
    const withPrefix = prefix && !s.startsWith(prefix) ? prefix + s : s;
    return suffix && !withPrefix.endsWith(suffix) ? withPrefix + suffix : withPrefix;
  };
  const curStr = addSign(cur);
  if (prev == null) return <span dangerouslySetInnerHTML={{ __html: curStr }} />;
  return <span dangerouslySetInnerHTML={{ __html: `${curStr} (${addSign(prev)})` }} />;
};

const SectionHeader = ({ num, title }: { num?: number; title: string }) => (
  <div style={s.sectionHeader}>
    <span style={s.sectionTitle}>{num != null ? `${num}. ` : ''}<H html={title} /></span>
  </div>
);

const WeeklyReportView: React.FC<WeeklyReportViewProps> = ({ data }) => {
  if (!data) return null;
  const { reportMetadata, section_1, section_2, section_3, section_4, section_5, section_6 } = data;

  return (
    <div style={s.outer}>
      <div style={s.container}>

        {/* Header */}
        <div style={s.headerBlock}>
          <img src={PeopleLensLogo} style={s.logoImg} alt="PeopleLens" />
        </div>

        <div style={s.body}>
          {/* Greeting */}
          {reportMetadata?.greeting && <p style={s.greeting}><H html={reportMetadata.greeting} /></p>}
          {reportMetadata?.introLine1 && <p style={s.greeting}><H html={reportMetadata.introLine1} /></p>}
          {reportMetadata?.introLine2 && <p style={{ ...s.greeting, marginBottom: 0 }}><H html={reportMetadata.introLine2} /></p>}

          {/* Section 1: Funnel Summary */}
          {section_1 && (
            <>
              <SectionHeader num={1} title={section_1.sectionTitle} />
              {section_1.note && <p style={{ fontSize: 11, fontWeight: 400, color: '#6b7280', fontStyle: 'italic', margin: '4px 0 12px' }}>{section_1.note}</p>}
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>AE</th>
                      <th style={s.th}>Total Deals</th>
                      <th style={s.th}>Closed Won</th>
                      <th style={s.th}>Closed Lost</th>
                      <th style={s.th}>Total Calls</th>
                      <th style={s.th}>Total Bookings</th>
                      <th style={s.th}>Win Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={s.tdBold}>{section_1.current?.ae || '—'}</td>
                      <td style={s.td}><FmtCell cur={section_1.current?.totalDeals} prev={section_1.previous?.totalDeals} /></td>
                      <td style={s.td}><FmtCell cur={section_1.current?.closedWon} prev={section_1.previous?.closedWon} /></td>
                      <td style={s.td}><FmtCell cur={section_1.current?.closedLost} prev={section_1.previous?.closedLost} /></td>
                      <td style={s.td}><FmtCell cur={section_1.current?.totalCalls} prev={section_1.previous?.totalCalls} /></td>
                      <td style={s.td}><FmtCell cur={section_1.current?.totalBookings} prev={section_1.previous?.totalBookings} prefix="$" /></td>
                      <td style={s.td}><FmtCell cur={section_1.current?.winRate} prev={section_1.previous?.winRate} suffix="%" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Section 2: Winning Behaviors */}
          {section_2 && (
            <>
              <SectionHeader num={2} title={section_2.sectionTitle} />
              <ul style={s.ul}>
                {section_2.behaviors?.map((b: any, i: number) => (
                  <li key={i} style={s.li}><H html={b.title} />{b.description ? ': ' : ''}<H html={b.description} /></li>
                ))}
              </ul>
            </>
          )}

          {/* Section 3: Deal Risks */}
          {section_3 && (
            <>
              <SectionHeader num={3} title={section_3.sectionTitle} />
              <ul style={s.ul}>
                {section_3.risks?.map((r: any, i: number) => (
                  <li key={i} style={s.li}><H html={r.risk} />{r.description ? ': ' : ''}<H html={r.description} /></li>
                ))}
              </ul>
            </>
          )}

          {/* Section 4: AE Performance Insights */}
          {section_4 && (
            <>
              <SectionHeader num={4} title={section_4.sectionTitle} />
              {section_4.repPattern && (
                <p style={{ fontSize: 13, fontWeight: 400, color: '#171F46', marginBottom: 12, lineHeight: 1.6 }}>
                  <b><H html={section_4.repPattern} />{section_4.overview ? ':' : ''}</b>
                  {section_4.overview && <> <H html={section_4.overview} /></>}
                </p>
              )}
              {section_4.action && (
                <div>
                  {section_4.action.label && (
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#171F46', marginBottom: 10 }}>{section_4.action.label}</p>
                  )}
                  {[
                    { icon: NUDGE_ICON, label: 'Nudge', text: section_4.action.nudge },
                    { icon: INSIGHT_ICON, label: 'Insight', text: section_4.action.insight },
                    { icon: IMPACT_ICON, label: 'Impact', text: section_4.action.impact },
                  ].filter(item => item.text).map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#eef0f8', borderRadius: 10, padding: '8px 10px', minWidth: 58, flexShrink: 0 }}>
                        <img src={item.icon} style={{ width: 26, height: 26, objectFit: 'contain' }} alt={item.label} />
                        <span style={{ fontSize: 10, fontWeight: 600, color: '#374151', marginTop: 0 }}>{item.label}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 400, color: '#374151', lineHeight: 1.55 }}>
                        <H html={item.text} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Section 5: Discovery */}
          {section_5 && (
            <>
              <SectionHeader num={5} title={section_5.sectionTitle} />
              {(['WDW', 'IO'] as const).map((key) => {
                const entry = section_5[key];
                if (!entry) return null;
                const isWDW = key === 'WDW';
                const headerTitle = isWDW
                  ? `What's Done Well (WDW) - ${entry.component}`
                  : `Improvement Opportunities (IO) - ${entry.component}`;
                const labelMain = isWDW ? 'WDW' : 'IO';
                const labelSnippet = isWDW ? 'Snippet (WDW)' : 'Snippet (IO)';
                return (
                  <div key={key} style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ background: '#f3f4f6', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', display: 'table', width: '100%', tableLayout: 'fixed' }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#171F46', display: 'table-cell', verticalAlign: 'middle', paddingRight: 10 }}>
                        {headerTitle}
                      </span>
                      <span style={{ display: 'table-cell', verticalAlign: 'middle', fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap', width: 130, textAlign: 'right' }}>
                        Score (1-10): {entry.score}
                      </span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                      <tbody>
                        {entry.description && (
                          <tr>
                            <td style={{ background: '#f3f4f6', fontWeight: 700, width: 120, minWidth: 120, padding: '10px 12px', verticalAlign: 'top', fontSize: 12, lineHeight: 1.5, color: '#171F46' }}>
                              {labelMain}
                            </td>
                            <td style={{ background: '#ffffff', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', padding: '10px 12px', verticalAlign: 'top', fontSize: 12, lineHeight: 1.5, color: '#333' }}>
                              <H html={entry.description} />
                            </td>
                          </tr>
                        )}
                        {entry.snippet && (
                          <tr>
                            <td style={{ background: '#f3f4f6', fontWeight: 700, width: 120, minWidth: 120, padding: '10px 12px', verticalAlign: 'top', fontSize: 12, lineHeight: 1.5, color: '#171F46' }}>
                              {labelSnippet}
                            </td>
                            <td style={{ background: '#ffffff', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', padding: '10px 12px', verticalAlign: 'top', fontSize: 12, lineHeight: 1.5, color: '#333' }}>
                              <em>"{entry.snippet}"</em>
                              {(entry.external_transcripts_link || entry.timestamp || entry.account) && (
                                <div style={{ marginTop: 4, fontSize: 11, color: '#374151' }}>
                                  {entry.external_transcripts_link && (
                                    <a href={entry.external_transcripts_link} target="_blank" rel="noopener noreferrer" style={{ color: '#0B69FF', textDecoration: 'underline', fontSize: 12 }}>[Link]</a>
                                  )}
                                  {entry.timestamp && <span> ({entry.timestamp})</span>}
                                  {entry.account && <span> ({entry.account})</span>}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                        {!isWDW && entry.idealResponse && (
                          <tr>
                            <td style={{ background: '#f3f4f6', fontWeight: 700, width: 120, minWidth: 120, padding: '10px 12px', verticalAlign: 'top', fontSize: 12, lineHeight: 1.5, color: '#171F46' }}>
                              Snippet (IO) - Ideal
                            </td>
                            <td style={{ background: '#ffffff', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', padding: '10px 12px', verticalAlign: 'top', fontSize: 12, lineHeight: 1.5, color: '#333', fontStyle: 'italic' }}>
                              "{entry.idealResponse}"
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })}

              {/* Rep Nudge — inside section_5.IO */}
              {section_5?.IO?.repNudges?.length > 0 && (
                <div style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ background: '#f3f4f6', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 13, color: '#171F46' }}>Rep Nudge</div>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <tbody>
                      <tr>
                        <td style={{ background: '#f3f4f6', fontWeight: 700, width: 120, minWidth: 120, padding: '10px 12px', verticalAlign: 'top', fontSize: 12, color: '#171F46' }}>Rep Nudge</td>
                        {section_5.IO.repNudges.map((item: any, i: number) => (
                          <td key={i} style={{ background: '#ffffff', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', padding: '10px 12px', verticalAlign: 'top', fontSize: 12, color: '#333' }}>
                            <H html={String(item).replace(/<\/b>\s*/i, '</b><br/>')} />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Manager Nudge — inside section_5.IO */}
              {section_5?.IO?.managerNudges?.length > 0 && (
                <div style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ background: '#f3f4f6', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 13, color: '#171F46' }}>Manager Nudge</div>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <tbody>
                      <tr>
                        <td style={{ background: '#f3f4f6', fontWeight: 700, width: 120, minWidth: 120, padding: '10px 12px', verticalAlign: 'top', fontSize: 12, color: '#171F46' }}>Manager Nudge</td>
                        {section_5.IO.managerNudges.map((item: any, i: number) => (
                          <td key={i} style={{ background: '#ffffff', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', padding: '10px 12px', verticalAlign: 'top', fontSize: 12, color: '#333' }}>
                            <H html={String(item).replace(/<\/b>\s*/i, '</b><br/>')} />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Section 6: Bottom Line */}
          {section_6 && (
            <>
              <SectionHeader num={6} title={section_6.sectionTitle} />
              <ul style={s.ul}>
                {[
                  { label: 'The Win', value: section_6.theWin },
                  { label: 'The Friction', value: section_6.theFriction },
                  { label: 'The Priority', value: section_6.thePriority },
                ].map(({ label, value }) =>
                  value ? (
                    <li key={label} style={s.li}>
                      <b>{label}:</b> <H html={value} />
                    </li>
                  ) : null
                )}
              </ul>
              {section_6.closingNote && (
                <p style={{ fontSize: 13, fontWeight: 400, color: '#171F46', margin: '4px 0 24px 0', fontStyle: 'italic' }}>
                  <H html={section_6.closingNote} />
                </p>
              )}
              {section_6.signature && (
                <p style={s.signature}>
                  {section_6.signature.closing},<br />{section_6.signature.signedBy}
                </p>
              )}
            </>
          )}

          {/* Footer */}
          <div style={{ marginTop: 16 }}>
            <a href="https://regology-peoplelens.web.app/" target="_blank" rel="noopener noreferrer" style={s.loginBtn}>Login</a>
            <a href="https://peoplelens.ai/resources/DiscoveryFramework.pdf" target="_blank" rel="noopener noreferrer" style={s.resourceBtn}>View Resources</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyReportView;
